import { useEffect, useState } from 'react'
import { CloudOff } from 'lucide-react'
import { pendingOfflineOrders, type OfflineOrder } from '@/lib/offline/offlineStore'

/**
 * Work still sitting in THIS terminal's offline queue at Day Close
 * (F-CTRL-N55).
 *
 * WHY A CLIENT-SIDE CHECK RATHER THAN A CLOSE BLOCKER
 *
 * The server-side guard (invariant #12) blocks the close while any row is open
 * in `offline_unreconciled_items` — but a queued offline order has not reached
 * the server at all. It is a record in one machine's IndexedDB, and no backend
 * can know it exists. So the honest guarantee is narrower than "the day cannot
 * close with unsynced work": it is "the terminal you are closing FROM tells you
 * what it is still holding".
 *
 * That is worth having anyway, because Day Close is done at the counter, which
 * is the terminal that took the offline orders. And it matters more now that
 * offline capture can take payment: an entry sitting here after an outage may be
 * cash that is physically in the drawer with no order behind it yet, which is
 * precisely the discrepancy the manager is about to try to reconcile.
 *
 * A warning, not a blocker. The queue drains by itself once the connection is
 * back, and refusing to close a day over a terminal-local record would be a
 * blocker a manager cannot clear — the worst kind. What they need is to know to
 * wait a moment before counting the till.
 */
export function PendingOfflineSyncAlert() {
  const [pending, setPending] = useState<OfflineOrder[]>([])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const all = await pendingOfflineOrders()
      if (!cancelled) setPending(all)
    }
    void load()
    // Re-read on a timer: the queue drains in the background, and a manager
    // watching this screen should see it clear rather than have to reload.
    const id = window.setInterval(() => void load(), 5000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [])

  if (pending.length === 0) return null

  const paid = pending.filter((p) => p.payment)
  const collected = paid.reduce((sum, p) => sum + (p.payment?.amount ?? 0), 0)

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950">
      <div className="flex items-start gap-3">
        <CloudOff
          className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
          aria-hidden="true"
        />
        <div className="text-sm text-amber-900 dark:text-amber-200">
          <p className="font-semibold">
            {pending.length} offline order{pending.length === 1 ? '' : 's'} on this terminal
            {' '}have not synced yet
          </p>
          <p className="mt-1">
            They sync by themselves once the connection is back. Wait for this to clear
            before counting the till, or the totals below will not include them.
          </p>
          {paid.length > 0 && (
            <p className="mt-1 font-medium">
              {paid.length} of them {paid.length === 1 ? 'was' : 'were'} already paid —
              about {collected.toFixed(2)} is in the drawer with no order behind it yet.
            </p>
          )}
          <p className="mt-2 text-xs opacity-80">
            Only this terminal is checked. Another till that took orders offline holds its
            own queue.
          </p>
        </div>
      </div>
    </div>
  )
}
